// src/hooks/useSocialLanguage.js
//
// GLOBAL app language (en | ar), shared across every component that uses this hook via a tiny
// external store. One place (e.g. the top-bar toggle) flips the language and ALL pages update and
// stay in sync. The choice persists in localStorage and only changes when the user changes it.
// It also sets <html lang/dir> so the whole app flips between LTR and RTL.

import { useSyncExternalStore, useCallback } from 'react';

const STORAGE_KEY = 'socialLanguage';

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
};

let currentLanguage = readStored();
const listeners = new Set();

const applyDocument = (lang) => {
  try {
    const el = document.documentElement;
    el.setAttribute('lang', lang);
    el.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  } catch {
    /* no document — ignore */
  }
};
// Apply once on load so direction matches the stored choice immediately.
applyDocument(currentLanguage);

const setGlobalLanguage = (lang) => {
  const next = lang === 'ar' ? 'ar' : 'en';
  if (next === currentLanguage) return;
  currentLanguage = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  applyDocument(next);
  listeners.forEach((l) => l());
};

// Keep multiple tabs in sync.
try {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      const v = e.newValue === 'ar' ? 'ar' : 'en';
      if (v !== currentLanguage) { currentLanguage = v; applyDocument(v); listeners.forEach((l) => l()); }
    }
  });
} catch { /* ignore */ }

const subscribe = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };
const getSnapshot = () => currentLanguage;

export const useSocialLanguage = () => {
  const language = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setLanguage = useCallback((lang) => setGlobalLanguage(lang), []);
  const toggleLanguage = useCallback(() => setGlobalLanguage(currentLanguage === 'en' ? 'ar' : 'en'), []);
  return { language, setLanguage, toggleLanguage, isRTL: language === 'ar' };
};

export default useSocialLanguage;
